import { defineNuxtPlugin, useCookie, useRuntimeConfig, useRequestURL } from "#app";
import { watch } from "vue";
import { useTelaSession } from "./composables/session.js";
import { createNuxtAuthClient } from "./shared.js";
import { useRoute, navigateTo } from "#imports";
export default defineNuxtPlugin({
  name: "tela-auth",
  enforce: "pre",
  env: {
    islands: false
  },
  async setup() {
    const authConfig = useRuntimeConfig().public.telaAuth;
    const { session, user, getToken } = useTelaSession();
    const tokenCookie = useCookie(authConfig.jwtCookieName);
    tokenCookie.value = null;
    const authClient = createNuxtAuthClient(authConfig.apiUrl, () => tokenCookie.value ?? null);
    const query = useRoute().query;
    const nonce = query.nonce;
    const origin = useRequestURL();
    if (import.meta.server) {
      if (nonce && typeof nonce === "string") {
        if (nonce !== "null") {
          try {
            const payload = await authClient.session.getNoncePayload(nonce);
            tokenCookie.value = payload.token;
          } catch {
            await navigateTo(`${authConfig.apiUrl}/api/auth/handshake?origin=${origin.toString()}`, {
              external: true,
              redirectCode: 302
            });
            return;
          }
        }
      } else {
        await navigateTo(`${authConfig.apiUrl}/api/auth/handshake?origin=${origin.toString()}`, {
          external: true,
          redirectCode: 302
        });
        return;
      }
    } else if (import.meta.client && !session.value) {
      const token = await getToken();
      if (token) {
        tokenCookie.value = token;
      }
    }
    async function initializeSession() {
      if (session.value && user.value)
        return;
      const sessionData = await authClient.session.getSession();
      if (!sessionData) {
        return;
      }
      user.value = sessionData.user;
      session.value = sessionData.session;
    }
    if (tokenCookie.value) {
      await initializeSession();
    }
    if (import.meta.client) {
      let tokenRefreshInterval = null;
      watch(session, async (newVal) => {
        if (tokenRefreshInterval) {
          clearTimeout(tokenRefreshInterval);
          tokenRefreshInterval = null;
        }
        if (newVal) {
          async function createTokenRefreshInterval() {
            const token = await getToken();
            if (token) {
              tokenCookie.value = token;
            }
            tokenRefreshInterval = window.setTimeout(createTokenRefreshInterval, 6e4);
          }
          createTokenRefreshInterval();
        }
      }, { immediate: true });
    }
  },
  hooks: {
    "app:mounted": () => {
      const url = new URL(window.location.href);
      url.searchParams.delete("session");
      url.searchParams.delete("session_data");
      url.searchParams.delete("nonce");
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    }
  }
});
